/**
 * AdtExtras — Composants spécifiques à l'Autorisation de Travail
 * - Bloc S : Organigramme de commandement (LAFARGE / EE Niveau 1 / SST Niveau 2 max)
 * - Bloc T : Identification des interférences (3 lignes min, manuel)
 * - EmergencyBanner : Numéro d'urgence interne du site
 * - SignatureEngagementText : mention rouge engagement signature
 */
import { useEffect } from 'react';
import { Plus, Trash2, AlertTriangle, Phone } from 'lucide-react';

export function EmergencyBanner({ site }) {
  if (!site) return null;
  return (
    <div className="border-2 border-red-600 bg-red-50 px-4 py-3 flex items-center gap-3" data-testid="emergency-banner">
      <Phone className="w-5 h-5 text-red-700 flex-shrink-0" />
      <div>
        <div className="text-[10px] font-bold uppercase tracking-wider text-red-700">Numéro d'urgence interne du site</div>
        <div className="text-2xl font-mono font-bold text-red-700">{site.emergency || '—'}</div>
        <div className="text-[10px] text-stone-600">{site.name} ({site.code})</div>
      </div>
    </div>
  );
}

export function SignatureEngagementText() {
  return (
    <div className="border-2 border-red-300 bg-red-50 p-4 my-4" data-testid="signature-engagement">
      <p className="text-sm text-red-700 font-semibold leading-relaxed text-center">
        La signature vaut engagement à <strong className="uppercase">respecter</strong> et <strong className="uppercase">connaître</strong> les
        mesures de sécurité définies dans l'analyse de risque, le plan de prévention et le mode opératoire
        et à assurer la <strong className="uppercase">transmission</strong> de ces informations à <strong className="uppercase">tous</strong> les membres
        des équipes intervenantes.
      </p>
    </div>
  );
}

export function OrganigrammeBloc({ value, onChange, correspondantLafarge, entrepriseExterieure, correspondantEE }) {
  const v = value || { lafarge: '', ee_niveau1: '', ee_niveau1_corresp: '', sst_niveau2: [{}, {}, {}, {}, {}] };
  const sst = v.sst_niveau2 || [{}, {}, {}, {}, {}];
  while (sst.length < 5) sst.push({});

  // Auto-sync depuis les valeurs amont (Correspondant Lafarge + entreprise extérieure + correspondant EE) si vide
  useEffect(() => {
    const patch = {};
    if (!v.lafarge && correspondantLafarge) patch.lafarge = correspondantLafarge;
    if (!v.ee_niveau1 && entrepriseExterieure) patch.ee_niveau1 = entrepriseExterieure;
    if (!v.ee_niveau1_corresp && correspondantEE) patch.ee_niveau1_corresp = correspondantEE;
    if (Object.keys(patch).length > 0) onChange({ ...v, ...patch });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [correspondantLafarge, entrepriseExterieure, correspondantEE]);

  const update = (key, val) => onChange({ ...v, [key]: val });
  const updateSst = (i, k, val) => {
    const next = [...sst];
    next[i] = { ...next[i], [k]: val };
    onChange({ ...v, sst_niveau2: next });
  };

  const inp = (val, onChangeFn, placeholder, testid) => (
    <input type="text" value={val || ''} onChange={(e) => onChangeFn(e.target.value)}
      placeholder={placeholder}
      data-testid={testid}
      className="w-full px-2 py-2 border border-stone-300 focus:border-[#09090B] outline-none text-sm bg-white" />
  );

  return (
    <div className="border-2 border-amber-700">
      <div className="bg-amber-700 text-white px-3 py-2 text-[11px] font-bold uppercase tracking-wider flex items-center justify-between">
        <span>S — Organigramme de commandement</span>
        <span className="text-[10px] italic">Sous-traitance Niveau 2 maximum</span>
      </div>
      <div className="p-4 grid md:grid-cols-2 gap-6 bg-amber-50">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-wider mb-1 text-stone-600">LAFARGE</div>
          <div className="border border-stone-400 p-2 bg-white text-center mb-2 text-xs font-bold">Chargé de travaux Lafarge</div>
          {inp(v.lafarge, (x) => update('lafarge', x), 'Nom Prénom (Lafarge)', 'org-lafarge')}
        </div>
        <div>
          <div className="text-[10px] font-bold uppercase tracking-wider mb-1 text-stone-600">SST Niveau 1 — Entreprise principale</div>
          <div className="border border-stone-400 p-2 bg-white text-center mb-2 text-xs font-bold uppercase">
            {v.ee_niveau1 || entrepriseExterieure || <span className="text-stone-400 italic normal-case">Sélectionnez une entreprise extérieure</span>}
          </div>
          {inp(v.ee_niveau1_corresp, (x) => update('ee_niveau1_corresp', x), "Nom Prénom du Correspondant travaux EE", 'org-ee1-corresp')}
        </div>
        <div className="md:col-span-2">
          <div className="text-[10px] font-bold uppercase tracking-wider mb-2 text-stone-600">SST Niveau 2 (max 5 sous-traitants)</div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
            {sst.map((s, i) => (
              <div key={i} className="border border-stone-400 p-2 bg-white space-y-2" data-testid={`org-sst-${i}`}>
                <div className="text-center text-[10px] font-bold uppercase tracking-wider bg-amber-100 py-1">Entreprise Niveau 2</div>
                {inp(s.entreprise, (x) => updateSst(i, 'entreprise', x), 'Entreprise ?', `org-sst-${i}-entreprise`)}
                {inp(s.charge_travaux, (x) => updateSst(i, 'charge_travaux', x), 'Chargé de travaux ?', `org-sst-${i}-charge`)}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export function InterferencesBloc({ value, onChange }) {
  const list = value || [{}, {}, {}];
  while (list.length < 3) list.push({});

  const update = (i, k, val) => {
    const next = [...list];
    next[i] = { ...next[i], [k]: val };
    onChange(next);
  };
  const add = () => onChange([...list, {}]);
  const remove = (i) => onChange(list.filter((_, idx) => idx !== i));

  return (
    <div className="border-2 border-violet-700">
      <div className="bg-violet-700 text-white px-3 py-2 text-[11px] font-bold uppercase tracking-wider">
        T — Identification des interférences & coactivité
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-violet-100">
            <tr>
              <th className="text-left p-2 w-28 font-bold uppercase tracking-wider text-[10px]">Interférences</th>
              <th className="text-left p-2 font-bold uppercase tracking-wider text-[10px]">Description</th>
              <th className="text-left p-2 w-40 font-bold uppercase tracking-wider text-[10px]">Entre quels chantiers ?</th>
              <th className="text-left p-2 w-28 font-bold uppercase tracking-wider text-[10px]">Périodes ?</th>
              <th className="text-left p-2 font-bold uppercase tracking-wider text-[10px]">Mesures prises ?</th>
              <th className="text-left p-2 w-32 font-bold uppercase tracking-wider text-[10px]">Responsable ?</th>
              <th className="w-8 p-2"></th>
            </tr>
          </thead>
          <tbody>
            {list.map((it, i) => (
              <tr key={i} className="border-b border-violet-100 align-top">
                <td className="p-2 font-bold bg-violet-50">Interférence {i + 1}</td>
                {['description', 'chantiers', 'periodes', 'mesures', 'responsable'].map((k) => (
                  <td className="p-1" key={k}>
                    <textarea rows={2} className="w-full px-2 py-1 border border-stone-200 focus:border-[#09090B] outline-none text-sm resize-y"
                      data-testid={`interf-${i}-${k}`}
                      value={it[k] || ''} onChange={(e) => update(i, k, e.target.value)} />
                  </td>
                ))}
                <td className="p-1 text-center">
                  {list.length > 3 && (
                    <button type="button" onClick={() => remove(i)} className="text-[#DA291C]">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="p-3">
        <button type="button" data-testid="interf-add"
          onClick={add}
          className="text-xs font-bold uppercase tracking-wider text-[#09090B] inline-flex items-center gap-1">
          <Plus className="w-4 h-4" /> Ajouter une interférence
        </button>
      </div>
    </div>
  );
}
